import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Crown, LogOut, X } from 'lucide-react';
import { isCompanyStaff } from '../../utils/roleUtils';
import { UPGRADE_MODAL_VARIANTS } from '../../context/CompanyAccessContext';

const TRIAL_PENDING_COPY = {
  [UPGRADE_MODAL_VARIANTS.TRIAL_PENDING]: {
    badge: 'Trial scheduled',
    title: 'Your trial has not started yet',
    body: 'Full access to jobs, candidates, and API imports will unlock automatically when your trial period begins.',
    primary: 'Got it',
    secondary: 'Not now',
    showLogout: false,
    dismissible: true,
    hideUpgrade: true,
  },
};

const SUBSCRIPTION_EXPIRED_COPY = {
  [UPGRADE_MODAL_VARIANTS.LOGIN]: {
    badge: 'Subscription expired',
    title: 'Your subscription has ended',
    body: 'Renew your plan to restore full access to jobs, candidates, analytics, and your recruitment workspace.',
    primary: 'Renew subscription',
    secondary: 'Continue browsing',
    showLogout: true,
    dismissible: true,
  },
};

const COPY = {
  [UPGRADE_MODAL_VARIANTS.LOGIN]: {
    badge: 'Subscription required',
    title: 'Your free trial has ended',
    body: 'Upgrade your plan to keep using JobScribe — jobs, candidates, analytics, and your full recruitment workspace unlock after checkout.',
    primary: 'Go to Plan & Billing',
    secondary: 'Continue browsing',
    showLogout: true,
    dismissible: true,
  },
  [UPGRADE_MODAL_VARIANTS.NAVIGATION]: {
    badge: 'Upgrade required',
    title: 'This feature needs an active plan',
    body: 'Your trial or subscription has ended. Upgrade your plan to access this section and continue recruiting with JobScribe.',
    primary: 'Upgrade plan',
    secondary: 'Not now',
    showLogout: false,
    dismissible: true,
  },
  [UPGRADE_MODAL_VARIANTS.API]: {
    badge: 'Upgrade required',
    title: 'Your plan has expired',
    body: 'This action is unavailable until you upgrade. Go to Plan & Billing to choose a plan and complete checkout.',
    primary: 'Go to Plan & Billing',
    secondary: 'Not now',
    showLogout: false,
    dismissible: true,
  },
};

const STAFF_COPY_OVERRIDES = {
  [UPGRADE_MODAL_VARIANTS.LOGIN]: {
    title: 'Company subscription required',
    body: 'Your company’s trial or subscription has ended. Ask your company administrator to renew the plan so you can regain full access.',
    primary: 'Got it',
    hideUpgrade: true,
  },
  [UPGRADE_MODAL_VARIANTS.NAVIGATION]: {
    title: 'Access paused for your company',
    body: 'This section needs an active company subscription. Ask your company administrator to renew — staff accounts cannot complete checkout.',
    primary: 'Got it',
    hideUpgrade: true,
  },
  [UPGRADE_MODAL_VARIANTS.API]: {
    title: 'Company plan has expired',
    body: 'This action is unavailable until your company renews. Ask your company administrator to renew the subscription in Plan & Billing.',
    primary: 'Got it',
    hideUpgrade: true,
  },
};

const STAFF_SUBSCRIPTION_EXPIRED_OVERRIDE = {
  title: 'Company subscription has ended',
  body: 'Ask your company administrator to renew the plan so your team can restore access to JobScribe.',
  primary: 'Got it',
  hideUpgrade: true,
};

export default function TrialExpiredUpgradeModal({
  isOpen,
  variant = UPGRADE_MODAL_VARIANTS.NAVIGATION,
  subscriptionExpired = false,
  trialNotStarted = false,
  trialStartsAt = null,
  onUpgrade,
  onClose,
  onLogout,
}) {
  const isStaff = isCompanyStaff();
  const trialPendingCopy = TRIAL_PENDING_COPY[UPGRADE_MODAL_VARIANTS.TRIAL_PENDING];
  let copy =
    variant === UPGRADE_MODAL_VARIANTS.TRIAL_PENDING
      ? trialPendingCopy
      : COPY[variant] || COPY[UPGRADE_MODAL_VARIANTS.NAVIGATION];

  if (subscriptionExpired && variant === UPGRADE_MODAL_VARIANTS.LOGIN) {
    copy = SUBSCRIPTION_EXPIRED_COPY[variant];
  }

  if (isStaff && !copy.hideUpgrade) {
    const staffOverride =
      subscriptionExpired && variant === UPGRADE_MODAL_VARIANTS.LOGIN
        ? STAFF_SUBSCRIPTION_EXPIRED_OVERRIDE
        : STAFF_COPY_OVERRIDES[variant] || STAFF_COPY_OVERRIDES[UPGRADE_MODAL_VARIANTS.NAVIGATION];
    copy = { ...copy, ...staffOverride };
  }

  const formattedTrialStart = trialStartsAt
    ? new Date(trialStartsAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  if (typeof document === 'undefined') {
    return null;
  }

  const primaryAction = copy.hideUpgrade ? onClose : onUpgrade;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed inset-0 z-[120] flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/75 backdrop-blur-md"
            aria-hidden
            onClick={copy.dismissible ? onClose : undefined}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="trial-expired-title"
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-white/95 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.65)] backdrop-blur-xl"
          >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.2),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(245,158,11,0.15),transparent_45%)]" />

            {copy.dismissible ? (
              <button
                type="button"
                onClick={onClose}
                className="absolute right-4 top-4 z-10 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            ) : null}

            <div className="relative px-7 py-6">
              <div className="flex items-start gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 text-white shadow-lg shadow-orange-500/30">
                  <Crown size={20} strokeWidth={2.2} />
                </div>
                <div className="min-w-0 pr-6">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-600">
                    {copy.badge}
                  </p>
                  <h3 id="trial-expired-title" className="mt-1 text-xl font-bold text-slate-900">
                    {copy.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{copy.body}</p>
                  {trialNotStarted && formattedTrialStart ? (
                    <p className="mt-2 text-sm font-semibold text-indigo-700">
                      Trial begins {formattedTrialStart}
                    </p>
                  ) : null}
                  {isStaff && variant !== UPGRADE_MODAL_VARIANTS.TRIAL_PENDING ? (
                    <p className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <AlertCircle size={14} className="mt-0.5 shrink-0" />
                      Only your company administrator can renew or upgrade. Staff accounts cannot open
                      Plan &amp; Billing or complete checkout.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {copy.showLogout ? (
                  <button
                    type="button"
                    onClick={onLogout}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <LogOut size={16} />
                    Log out
                  </button>
                ) : !copy.hideUpgrade ? (
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    {copy.secondary}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={primaryAction}
                  className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/30 transition hover:from-indigo-700 hover:via-purple-700 hover:to-indigo-800"
                >
                  {copy.primary}
                </button>
              </div>

              {copy.showLogout ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full text-center text-xs font-medium text-slate-500 transition hover:text-slate-700"
                >
                  {copy.secondary}
                </button>
              ) : null}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
